/**
 * LandingArchetypePage — deep-dive marketing page for a single freelance
 * archetype (e.g. /para/desarrollador).
 *
 * Strategy
 * --------
 * Rather than copy-pasting the layout 8 times, this page is data-driven:
 * it reads from /src/data/landingArchetypeContent.js by slug and renders
 * the same structure for every persona. That keeps the design system
 * 100% consistent and means new archetypes (or copy edits) only touch
 * one file.
 *
 * Structure
 * ---------
 *   1. Header (same nav as Landing)
 *   2. Breadcrumb + Hero (persona-aware H1 + sub)
 *   3. "El problema" (3 pains as editorial cards)
 *   4. "Lo que ganas" (3 outcomes as benefit chips)
 *   5. "Las funcionalidades que importan para tú" (4 feature deep-dives)
 *   6. "Cómo lo usas" (3-step workflow)
 *   7. Inline quote (italic, no fake attribution)
 *   8. "Preguntas que te haces" (FAQ filtered for this persona)
 *   9. CTA + footer
 */
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useState } from "react";
import { toast } from "sonner";
import { useExperiment } from "../../hooks/useExperiment";
import { Button } from "../../components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../../components/ui/accordion";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { z } from "zod";
import { useZodForm, FormField, SubmitButton } from "../../components/form-ux";
import {
  ArrowRight, ArrowUpRight, Check, ShieldCheck, Sparkles, ChevronLeft, PlayCircle,
  Code2, Palette, Video, Briefcase, Scale, GraduationCap, ShoppingBag, Camera,
  Globe, Receipt, Bot, AlertCircle, LineChart, Coins, Target, TrendingUp,
  PiggyBank, Mail, MessageCircle,
} from "lucide-react";
import { LumfiMark, LumfiWordmark } from "../../components/LumfiLogo";
import { ArchetypeFeatureVisual } from "../../components/landing/ArchetypeFeatureVisual";
import { getArchetype } from "../../data/landingArchetypeContent";

const PERSONA_ICONS = {
  dev: Code2, design: Palette, content: Video, consult: Briefcase,
  legal: Scale, coach: GraduationCap, ecom: ShoppingBag, photo: Camera,
};

const FEATURE_ICONS = {
  Globe, Receipt, Bot, AlertCircle, LineChart, Coins, Target, TrendingUp,
  PiggyBank, Mail, MessageCircle,
};

function Eyebrow({ children, accent = "emerald" }) {
  const color = accent === "amber" ? "text-amber-700" : "text-emerald-700";
  return (
    <div className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold ${color}`}>
      <span className="h-px w-6 bg-current opacity-60" />
      {children}
    </div>
  );
}

export default function LandingArchetypePage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const archetype = getArchetype(lang, slug);
  // iter-212 — A/B experiment hook MUST be called before any early
  // return to comply with rules of hooks. The variant decides whether
  // landing visuals render as focused mocks or real screenshots.
  const { recordConversion: recordSignupConversion } = useExperiment(
    "landing-mock-vs-screenshot",
    { defaultVariant: "mock" },
  );
  const trackSignupClick = (placement) => recordSignupConversion({
    slug,
    placement,
  });

  if (!archetype) {
    // Unknown slug → soft 404 back to the main landing. Declarative
    // <Navigate /> avoids the React "navigate during render" warning.
    return <Navigate to="/" replace />;
  }

  const PersonaIcon = PERSONA_ICONS[archetype.key] || Briefcase;
  // Canonical URL: use the actual origin so preview, staging and prod
  // each get the right canonical without env-var plumbing.
  const SITE = typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "https://lumfi.wintimeapp.com";
  const pageUrl = `${SITE}/for/${archetype.slug}`;
  // Dynamic per-archetype OG image — served by FastAPI /api/public/og/{slug}.png.
  // Uses the same origin so WhatsApp/Twitter/Slack can pull it without
  // cross-origin CORS headaches.
  const ogImage = `${SITE}/api/public/og/${archetype.slug}.png`;
  const seoTitle = `${archetype.who} · Lumfi Cash Pilot`;
  const seoDesc = archetype.heroSub;

  return (
    <div
      className="min-h-screen bg-cream-100 text-slate-900 relative overflow-x-hidden"
      data-testid={`archetype-page-${archetype.slug}`}
    >
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={ogImage} />
        {/* JSON-LD: WebPage + Audience. Tells Google/Perplexity/Bing
            "this page is specifically about and FOR this persona",
            unlocking rich-result eligibility and persona-targeted
            answers in Generative Engine results. */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": seoTitle,
            "url": pageUrl,
            "description": seoDesc,
            "inLanguage": lang,
            "primaryImageOfPage": { "@type": "ImageObject", "url": ogImage, "width": 1200, "height": 630 },
            "isPartOf": {
              "@type": "WebSite",
              "name": "Lumfi Cash Pilot",
              "url": SITE,
            },
            "about": {
              "@type": "SoftwareApplication",
              "name": "Lumfi Cash Pilot",
              "applicationCategory": "FinanceApplication",
              "operatingSystem": "Web, iOS, Android (PWA)",
              "url": SITE,
            },
            "audience": {
              "@type": "Audience",
              "audienceType": archetype.who,
              "name": archetype.who,
              "geographicArea": {
                "@type": "AdministrativeArea",
                "name": "Latin America",
              },
            },
            "potentialAction": {
              "@type": "TryAction",
              "name": tx(lang, "Try live demo", "Ver el demo en vivo", "Ver o demo ao vivo"),
              "target": `${SITE}/for/${archetype.slug}#live-demo`,
            },
          })}
        </script>
      </Helmet>

      {/* ambient halos — same as Landing */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-[480px] w-[480px] rounded-full bg-emerald-200/35 blur-3xl landing-ambient" />
        <div className="absolute top-[480px] -left-32 h-[420px] w-[420px] rounded-full bg-amber-200/30 blur-3xl landing-ambient" style={{ animationDelay: "3s" }} />
        <div className="absolute inset-0 grain-overlay opacity-50" />
      </div>

      {/* NAV (lightweight version of Landing's nav) */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-cream-100/80 border-b border-cream-300/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="archetype-nav-home">
            <LumfiWordmark height={32} className="hidden sm:block" />
            <LumfiMark size={36} className="sm:hidden" />
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600">
            <Link to="/#features" className="hover:text-slate-900 transition-colors">Funcionalidades</Link>
            <Link to="/#pricing" className="hover:text-slate-900 transition-colors">Precios</Link>
            <Link to="/#faq" className="hover:text-slate-900 transition-colors">FAQ</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button data-testid="archetype-nav-login" variant="ghost" className="text-slate-700 hover:text-slate-900">
                {tx(lang, "Sign in", "Iniciar sesión", "Entrar")}
              </Button>
            </Link>
            <Link to="/register" className="hidden sm:block">
              <Button data-testid="archetype-nav-register" className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-5">
                {tx(lang, "Sign up", "Crear cuenta", "Criar conta")}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-6">
        <Link
          to="/#archetypes"
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 transition-colors"
          data-testid="archetype-breadcrumb-back"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          {tx(lang, "All archetypes", "Volver a todos los perfiles", "Voltar aos perfis")}
        </Link>
      </div>

      {/* HERO */}
      <section className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-6 sm:pt-10 lg:pt-16 pb-12 sm:pb-16 lg:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 sm:gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7 fade-up">
            {/* Persona target banner — must be unmistakable so the
                visitor knows in 1 second this page is for them. */}
            <div
              className="relative inline-flex items-center gap-4 bg-slate-900 text-white pl-3 pr-5 py-3 rounded-2xl shadow-[0_18px_40px_-20px_rgba(15,23,42,0.45)] max-w-full"
              data-testid="archetype-persona-banner"
            >
              <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center shrink-0">
                <PersonaIcon className="h-6 w-6 sm:h-7 sm:w-7 text-emerald-300" strokeWidth={1.6} />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-300">
                  {tx(lang, "This page is for", "Esta página es para", "Esta página é para")}
                </div>
                <div className="font-serif-display text-xl sm:text-2xl lg:text-[1.65rem] leading-tight font-medium mt-0.5 truncate">
                  {tx(lang, "You — a ", "Tú que eres ", "Você que é ")}
                  <span className="text-emerald-300">{archetype.who.toLowerCase()}</span>
                </div>
                <div className="mt-1 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-300">
                  <span className="h-1 w-1 rounded-full bg-emerald-400" />
                  {archetype.tag}
                </div>
              </div>
            </div>

            <h1 className="font-serif-display mt-7 text-4xl sm:text-5xl lg:text-[4.25rem] leading-[1.02] sm:leading-[0.98] font-medium text-slate-900">
              {archetype.heroTitle}
            </h1>
            <p className="mt-6 sm:mt-7 text-base sm:text-lg leading-relaxed text-slate-600 max-w-2xl">
              {archetype.heroSub}
            </p>
            <div className="mt-7 sm:mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/register" onClick={() => trackSignupClick("hero-cta")}>
                <Button
                  data-testid="archetype-hero-cta"
                  size="lg"
                  className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white text-base px-7 py-6 rounded-full gap-2 shadow-[0_8px_30px_-8px_rgba(22,163,74,0.55)] hover:translate-y-[-2px] transition-all"
                >
                  {archetype.cta} <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              {/* Demo request CTA — captures email so an admin can
                  approve and assign a personalized demo. Removes the
                  previous "instant public demo" path. */}
              <DemoRequestForm slug={archetype.slug} lang={lang} />
            </div>
            <div className="mt-7 sm:mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600" />{tx(lang, "No credit card", "Sin tarjeta", "Sem cartão")}</span>
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600" />{tx(lang, "Free forever plan", "Plan gratuito para siempre", "Plano grátis para sempre")}</span>
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600" />{tx(lang, "4 minutes setup", "4 minutos de setup", "4 min de setup")}</span>
            </div>
          </div>

          {/* Inline quote card */}
          <div className="lg:col-span-5 fade-up-2">
            <div className="relative bg-slate-900 text-white rounded-3xl p-8 lg:p-10 overflow-hidden">
              <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-emerald-500/15 blur-3xl" />
              <Sparkles className="h-5 w-5 text-emerald-400" />
              <p className="relative mt-5 font-serif-display text-xl sm:text-2xl leading-snug text-slate-100 italic">
                {archetype.quote}
              </p>
              <div className="relative mt-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-bold">
                <span className="h-px w-8 bg-emerald-400/50" />
                {tx(lang, "Voice of the persona", "Voz del perfil", "Voz do perfil")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PAINS */}
      <section className="relative bg-cream-200/70 border-y border-cream-300">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="max-w-3xl">
            <Eyebrow accent="amber">{tx(lang, "The pain", "Tu dolor real", "Sua dor real")}</Eyebrow>
            <h2 className="font-serif-display mt-4 sm:mt-5 text-3xl sm:text-4xl lg:text-5xl leading-[1.05] font-medium">
              {tx(lang, "What hurts on Mondays", "Lo que te incomoda los lunes", "O que te incomoda nas segundas")}
            </h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5 sm:gap-6">
            {archetype.pains.map((p, i) => (
              <div
                key={p.title}
                className="relative bg-white border border-cream-300 rounded-3xl p-7 hover:border-amber-300 hover:shadow-[0_18px_48px_-24px_rgba(15,23,42,0.18)] transition-all"
                data-testid={`archetype-pain-${i}`}
              >
                <span className="font-serif-display text-4xl text-amber-500/60 leading-none">0{i+1}</span>
                <h3 className="mt-4 font-serif-display text-xl font-medium leading-tight">{p.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GAINS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
        <div className="max-w-3xl">
          <Eyebrow>{tx(lang, "What you gain", "Lo que ganas con Lumfi", "O que você ganha com Lumfi")}</Eyebrow>
          <h2 className="font-serif-display mt-4 sm:mt-5 text-3xl sm:text-4xl lg:text-5xl leading-[1.05] font-medium">
            {tx(lang, "Real outcomes, not promises", "Resultados concretos, no promesas", "Resultados concretos, não promessas")}
          </h2>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-5 sm:gap-6">
          {archetype.gains.map((g, i) => (
            <div
              key={g.title}
              className="relative bg-white border border-cream-300 rounded-3xl p-7 hover:border-emerald-300 hover:shadow-[0_18px_48px_-24px_rgba(15,23,42,0.18)] transition-all"
              data-testid={`archetype-gain-${i}`}
            >
              <div className="h-10 w-10 rounded-2xl bg-emerald-100 flex items-center justify-center">
                <Check className="h-5 w-5 text-emerald-700" strokeWidth={2.2} />
              </div>
              <h3 className="mt-5 font-serif-display text-xl font-medium leading-tight">{g.title}</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{g.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features-archetype" className="relative bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 grain-overlay opacity-30" />
        <div className="absolute top-0 left-1/3 h-80 w-80 rounded-full bg-emerald-500/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-28">
          <div className="max-w-3xl">
            <Eyebrow>{tx(lang, "What you use", "Las funcionalidades que importan para ti", "As funcionalidades que importam para você")}</Eyebrow>
            <h2 className="font-serif-display mt-4 sm:mt-5 text-3xl sm:text-4xl lg:text-5xl leading-[1.05] font-medium">
              {tx(lang, "Lumfi, calibrated for you", "Lumfi calibrado para tu caso", "Lumfi calibrado para o seu caso")}
            </h2>
          </div>
          <div className="mt-10 sm:mt-14 grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
            {archetype.features.map((f, i) => {
              const Icon = FEATURE_ICONS[f.icon] || Sparkles;
              return (
                <div
                  key={f.title}
                  className="relative rounded-3xl bg-white/[0.03] border border-white/10 p-7 backdrop-blur-sm hover:bg-white/[0.06] transition-all"
                  data-testid={`archetype-feature-${i}`}
                >
                  <div className="absolute -top-px -left-px h-px w-24 bg-gradient-to-r from-emerald-400/40 to-transparent" />
                  <div className="flex items-center gap-2.5">
                    <Icon className="h-6 w-6 text-emerald-300" strokeWidth={1.5} />
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300">
                      {tx(lang, "Feature", "Funcionalidad", "Funcionalidade")} · 0{i + 1}
                    </span>
                  </div>
                  <h3 className="mt-5 font-serif-display text-xl lg:text-2xl font-medium leading-tight">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-300/85">{f.body}</p>
                  {/* Screenshot mockup — same kind that powers the
                      product surface, on-brand and accurate to UI. */}
                  {f.visual && (
                    <div data-testid={`archetype-feature-visual-${i}`}>
                      <ArchetypeFeatureVisual kind={f.visual} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WORKFLOW */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-28">
        <div className="max-w-3xl">
          <Eyebrow accent="amber">{tx(lang, "How it works", "Cómo lo usas", "Como você usa")}</Eyebrow>
          <h2 className="font-serif-display mt-4 sm:mt-5 text-3xl sm:text-4xl lg:text-5xl leading-[1.05] font-medium">
            {tx(lang, "From sign-up to your first answer in 4 minutes", "Del registro a tu primer diagnóstico en 4 minutos", "Do cadastro ao primeiro diagnóstico em 4 minutos")}
          </h2>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-5 sm:gap-8">
          {archetype.workflow.map((w, i) => (
            <div
              key={w.step}
              className="relative bg-white border border-cream-300 rounded-3xl p-7 sm:p-8 hover:border-emerald-300 hover:shadow-[0_18px_48px_-24px_rgba(15,23,42,0.18)] transition-all"
              data-testid={`archetype-step-${i}`}
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <span className="font-serif-display text-6xl text-stone-200/90 leading-none">{w.step}</span>
              <h3 className="mt-4 font-serif-display text-xl sm:text-2xl font-medium leading-tight">{w.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{w.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-cream-200/70 border-y border-cream-300">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="text-center">
            <Eyebrow>{tx(lang, "FAQ for you", "Preguntas que te haces", "Perguntas que você se faz")}</Eyebrow>
            <h2 className="font-serif-display mt-4 sm:mt-5 text-3xl sm:text-4xl lg:text-5xl leading-tight font-medium">
              {tx(lang, "Specific to your case", "Específicas para tu caso", "Específicas para o seu caso")}
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-8 sm:mt-12">
            {archetype.faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`q${i}`} className="border-b border-stone-300/60" data-testid={`archetype-faq-${i}`}>
                <AccordionTrigger className="text-left text-base sm:text-lg font-serif-display font-medium py-5 sm:py-6 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm sm:text-base leading-relaxed pb-6 pr-2 sm:pr-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-28">
        <div className="relative rounded-[2rem] lg:rounded-[3rem] bg-slate-900 text-white overflow-hidden p-8 sm:p-12 lg:p-16">
          <div className="absolute inset-0 grain-overlay opacity-30" />
          <div className="absolute -top-40 -right-40 h-[420px] w-[420px] rounded-full bg-emerald-500/25 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-amber-400/15 blur-3xl" />
          <div className="relative max-w-2xl">
            <Eyebrow>{tx(lang, "Your turn", "Tu turno", "Sua vez")}</Eyebrow>
            <h2 className="font-serif-display mt-5 sm:mt-6 text-3xl sm:text-5xl lg:text-6xl leading-[1.05] font-medium">
              {tx(lang,
                "See your real cash this week",
                "Mira tu cash real esta semana",
                "Veja seu caixa real esta semana")}
            </h2>
            <p className="mt-5 sm:mt-6 text-slate-300 leading-relaxed text-base sm:text-lg max-w-xl">
              {tx(lang,
                "Free plan forever. No credit card. 4 minutes to your first diagnosis tailored to a " + archetype.who.toLowerCase() + ".",
                `Plan gratuito para siempre. Sin tarjeta. 4 minutos para tu primer diagnóstico calibrado para ${archetype.who.toLowerCase()}.`,
                `Plano grátis para sempre. Sem cartão. 4 minutos para o primeiro diagnóstico para ${archetype.who.toLowerCase()}.`)}
            </p>
            <Link to="/register" className="inline-block mt-8 sm:mt-10" onClick={() => trackSignupClick("final-cta")}>
              <Button
                data-testid="archetype-final-cta"
                size="lg"
                className="bg-emerald-500 hover:bg-emerald-400 text-white text-base px-9 py-7 rounded-full gap-2 shadow-[0_18px_48px_-12px_rgba(16,185,129,0.55)] hover:translate-y-[-2px] transition-all"
              >
                {archetype.cta} <ArrowUpRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* MINIMAL FOOTER */}
      <footer className="border-t border-cream-300 bg-cream-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <Link to="/" className="flex items-center gap-2.5 hover:text-slate-900 transition-colors">
            <LumfiMark size={28} />
            <span className="font-display text-base font-bold">Lumfi</span>
          </Link>
          <div className="flex items-center gap-2 text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
            <span>Private by design · © {new Date().getFullYear()} Lumfi</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/privacidad" className="hover:text-slate-900">{tx(lang, "Privacy", "Privacidad", "Privacidade")}</Link>
            <Link to="/terminos" className="hover:text-slate-900">{tx(lang, "Terms", "Términos", "Termos")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}


/**
 * DemoRequestForm — captures the visitor's email so an admin can
 * review and approve the request. Replaces the previous "instant
 * public demo" CTA (iter-198 pivot).
 *
 * UX: inline email field + submit button. After successful submit,
 * shows a confirmation state. Returns a 429 with the existing-status
 * message if the visitor already has a pending/approved/active demo
 * for the same email within the cooldown window (30 days).
 */
function DemoRequestForm({ slug, lang }) {
  const [submitted, setSubmitted] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const Schema = z.object({
    email: z.string().trim().min(1, tx(lang, "Email is required.", "Ingresa un email válido.", "Insira um email válido."))
      .email(tx(lang, "Please enter a valid email.", "Ingresa un email válido.", "Insira um email válido.")),
  });
  const form = useZodForm(Schema, { email: "" }, { formId: "archetype-demo-request" });

  const submit = form.handleSubmit(async (values) => {
    const cleaned = values.email.trim();
    try {
      await axios.post(
        `${API}/api/public/demo-request`,
        { email: cleaned, slug, lang, source: "archetype-page" },
        { withCredentials: false },
      );
      setSubmitted(true);
      toast.success(tx(lang,
        "Got it! We'll review your request shortly.",
        "¡Listo! Revisamos tu solicitud y te respondemos pronto.",
        "Pronto! Vamos revisar e responder em breve."));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429) {
        const status = (detail && typeof detail === "object" && detail.existing_status) || "pending";
        toast.message(
          tx(lang,
            `You already have a ${status} demo request. Check your inbox.`,
            `Ya tienes una solicitud ${status === "pending" ? "pendiente" : "registrada"}. Revisa tu email.`,
            `Você já tem uma solicitação. Verifique seu email.`),
          { duration: 6000 },
        );
        setSubmitted(true);
      } else if (err?.response?.status === 404) {
        toast.error(tx(lang, "Unknown archetype.", "Perfil desconocido.", "Perfil desconhecido."));
      } else {
        toast.error(tx(lang,
          "Couldn't submit. Try again in a moment.",
          "No pudimos registrar tu solicitud. Prueba en un momento.",
          "Não foi possível registrar. Tente de novo em instantes."));
      }
    }
  });

  if (submitted) {
    return (
      <div
        data-testid="archetype-demo-request-success"
        className="w-full sm:w-auto inline-flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-900 px-5 py-4 rounded-2xl"
      >
        <Check className="h-5 w-5 text-emerald-600 shrink-0" />
        <div className="text-sm leading-snug">
          <strong className="font-semibold">
            {tx(lang, "Request received.", "Solicitud recibida.", "Solicitação recebida.")}
          </strong>{" "}
          {tx(lang,
            "We'll email you when your demo is ready.",
            "Te avisamos por email cuando esté listo tu demo.",
            "Avisamos por email quando seu demo estiver pronto.")}
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="w-full sm:flex-1 flex flex-col sm:flex-row gap-2 sm:items-stretch"
      data-testid="archetype-demo-request-form"
      noValidate
    >
      <FormField
        name="email"
        className="flex-1 sm:min-w-[220px] space-y-0"
        error={form.errors.email && (form.touched.email || form.submitCount > 0) ? form.errors.email : undefined}
      >
        <FormField.Input
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder={tx(lang, "Your email", "Tu email", "Seu email")}
          data-testid="archetype-demo-request-email"
          className="h-[52px] px-5 rounded-full border-2 border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus-visible:ring-0 focus:border-slate-900 transition-colors text-base"
          {...form.getFieldProps("email")}
        />
      </FormField>
      <SubmitButton
        testid="archetype-demo-request-submit"
        submitting={form.submitting}
        size="lg"
        className="bg-slate-900 hover:bg-slate-800 text-white text-base px-6 py-6 rounded-full transition-all"
        loadingText={tx(lang, "Submitting…", "Enviando…", "Enviando…")}
      >
        <PlayCircle className="h-5 w-5" />
        {tx(lang, "Request a demo", "Solicitar demo", "Solicitar demo")}
      </SubmitButton>
    </form>
  );
}
